/** Fixed intervals that a five-field cron can express without rounding or
 * calendar drift. Values divide their containing field exactly; only one day
 * is valid because day-of-month steps reset at month boundaries. */
export const SCHEDULE_EVERY_VALUES = [
  "1m",
  "2m",
  "3m",
  "4m",
  "5m",
  "6m",
  "10m",
  "12m",
  "15m",
  "20m",
  "30m",
  "1h",
  "2h",
  "3h",
  "4h",
  "6h",
  "8h",
  "12h",
  "1d",
] as const

export type ScheduleEvery = (typeof SCHEDULE_EVERY_VALUES)[number]

const CRON_BY_EVERY = {
  "1m": "* * * * *",
  "2m": "*/2 * * * *",
  "3m": "*/3 * * * *",
  "4m": "*/4 * * * *",
  "5m": "*/5 * * * *",
  "6m": "*/6 * * * *",
  "10m": "*/10 * * * *",
  "12m": "*/12 * * * *",
  "15m": "*/15 * * * *",
  "20m": "*/20 * * * *",
  "30m": "*/30 * * * *",
  "1h": "0 * * * *",
  "2h": "0 */2 * * *",
  "3h": "0 */3 * * *",
  "4h": "0 */4 * * *",
  "6h": "0 */6 * * *",
  "8h": "0 */8 * * *",
  "12h": "0 */12 * * *",
  "1d": "0 0 * * *",
} as const satisfies Record<ScheduleEvery, string>

const EVERY_BY_CRON = new Map<string, ScheduleEvery>(
  SCHEDULE_EVERY_VALUES.map((every) => [CRON_BY_EVERY[every], every])
)

function isScheduleEvery(value: string): value is ScheduleEvery {
  return (SCHEDULE_EVERY_VALUES as readonly string[]).includes(value)
}

/** Canonicalize CLI `--every` sugar into the sole persisted representation. */
export function cronForEvery(input: string): string {
  const every = input.trim().toLowerCase()
  if (!isScheduleEvery(every)) {
    throw new Error(
      `--every ${JSON.stringify(input)} is not exactly expressible as five-field cron. Valid --every values: ${SCHEDULE_EVERY_VALUES.join(", ")}. Use --cron with --timezone for another cadence.`
    )
  }
  return CRON_BY_EVERY[every]
}

/** Recover the shorthand only for cron strings emitted by {@link cronForEvery}. */
export function everyForCron(input: string): ScheduleEvery | null {
  return EVERY_BY_CRON.get(input.trim().replace(/\s+/g, " ")) ?? null
}
